'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast, { Toaster } from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_PROMPT = `You are a helpful AI assistant for TranceformRIGHT, a premier IT staffing and consulting company serving Fortune 500 clients. 

Your role is to:
- Answer questions about IT staffing, executive search, contract staffing, and IT consulting services
- Help prospects understand how TranceformRIGHT can solve their talent challenges
- Provide information about the company's 20+ years of experience and 98% client satisfaction rate
- Guide visitors toward scheduling a consultation or contacting the team
- Be professional, knowledgeable, and concise

When visitors ask about pricing, specific roles, or want to get started, encourage them to contact the team at Info@tranceformright.com or visit the Contact page.

Keep responses concise (2-4 sentences) and professional.`;

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm TranceformRIGHT's AI assistant. How can I help you with your IT staffing or consulting needs today?",
    },
  ]);
  const [emailSent, setEmailSent] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    true
  );

  // Track if we're currently streaming a response
  const isStreamingRef = useRef(false);
  const lastResponseRef = useRef('');

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Handle streaming response updates
  useEffect(() => {
    if (isLoading && response) {
      isStreamingRef.current = true;
      lastResponseRef.current = response;
      // Update the last assistant message in real-time during streaming
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && isStreamingRef.current) {
          return [...prev.slice(0, -1), { role: 'assistant', content: response }];
        }
        return prev;
      });
    }
  }, [response, isLoading]);

  // When streaming completes, finalize the message
  useEffect(() => {
    if (!isLoading && isStreamingRef.current && response) {
      isStreamingRef.current = false;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return [...prev.slice(0, -1), { role: 'assistant', content: response }];
        }
        return prev;
      });
    }
  }, [isLoading, response]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages([...newMessages, { role: 'assistant', content: '' }]);
    setInput('');
    isStreamingRef.current = true;

    const apiMessages = [
      { role: 'system' as const, content: SYSTEM_PROMPT },
      ...newMessages.map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
    ];

    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 512 });

    // Show email prompt after 3 user messages
    const userMsgCount = newMessages.filter((m) => m.role === 'user').length;
    if (userMsgCount >= 3 && !emailSent && !showEmailPrompt) {
      setTimeout(() => setShowEmailPrompt(true), 2000);
    }
  };

  const handleSendEmail = async () => {
    if (isSendingEmail) return;
    setIsSendingEmail(true);
    try {
      const res = await fetch('/api/chat-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messages.filter((m) => m.content.trim()),
          userEmail: visitorEmail || undefined,
        }),
      });
      if (res.ok) {
        setEmailSent(true);
        setShowEmailPrompt(false);
        toast.success('Conversation forwarded to our team!');
      } else {
        toast.error('Could not forward conversation. Please try again.');
      }
    } catch {
      toast.error('Could not forward conversation. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const quickReplies = [
    'Tell me about IT staffing',
    'How does executive search work?',
    'I need contract staffing',
    'Schedule a consultation',
  ];

  return (
    <>
      <Toaster position="bottom-left" />

      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 text-white font-semibold shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #00A7FF 0%, #0091DD 100%)',
            borderRadius: '50px',
            padding: '14px 22px',
            boxShadow: '0 8px 32px rgba(0,145,221,0.45)',
          }}
          aria-label="Open chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <span className="text-sm">Chat with us</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 flex flex-col shadow-2xl transition-all duration-300"
          style={{
            width: '380px',
            maxWidth: 'calc(100vw - 24px)',
            height: isMinimized ? 'auto' : '560px',
            maxHeight: 'calc(100vh - 100px)',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(0,167,255,0.2)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #00A7FF 0%, #0091DD 100%)' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%' }}
              >
                TR
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">TranceformRIGHT AI</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-300 inline-block" />
                  <span className="text-white/80 text-xs">Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label={isMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMinimized ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div
                className="flex-1 overflow-y-auto p-4 flex flex-col gap-3"
                style={{ background: '#f8fbff' }}
              >
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div
                        className="flex items-center justify-center text-white font-bold text-xs flex-shrink-0 mr-2 self-end"
                        style={{ width: '28px', height: '28px', background: '#00A7FF', borderRadius: '50%' }}
                      >
                        TR
                      </div>
                    )}
                    <div
                      className="max-w-[78%] px-3 py-2.5 text-sm leading-relaxed"
                      style={{
                        borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: msg.role === 'user' ? 'linear-gradient(135deg, #00A7FF, #0091DD)' : 'white',
                        color: msg.role === 'user' ? 'white' : '#333',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      }}
                    >
                      {msg.content || (
                        <span className="flex gap-1 items-center py-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Quick Replies — show only at start */}
                {messages.length === 1 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {quickReplies.map((qr) => (
                      <button
                        key={qr}
                        onClick={() => {
                          setInput(qr);
                          setTimeout(() => inputRef.current?.focus(), 50);
                        }}
                        className="text-xs px-3 py-1.5 rounded-full border transition-colors hover:bg-blue-50"
                        style={{ borderColor: '#00A7FF', color: '#0091DD', background: 'white' }}
                      >
                        {qr}
                      </button>
                    ))}
                  </div>
                )}

                {/* Email prompt */}
                {showEmailPrompt && !emailSent && (
                  <div
                    className="rounded-xl p-3 text-sm"
                    style={{ background: '#EBF5FB', border: '1px solid #00A7FF33' }}
                  >
                    <p className="font-semibold text-gray-700 mb-2" style={{ fontSize: '13px' }}>
                      📧 Want our team to follow up?
                    </p>
                    <input
                      type="email"
                      value={visitorEmail}
                      onChange={(e) => setVisitorEmail(e.target.value)}
                      placeholder="Your email (optional)"
                      className="w-full px-3 py-1.5 rounded-lg border text-sm mb-2 outline-none focus:border-blue-400"
                      style={{ borderColor: '#cce5f5', fontSize: '13px' }}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSendEmail}
                        disabled={isSendingEmail}
                        className="flex-1 py-1.5 rounded-lg text-white text-xs font-semibold transition-opacity"
                        style={{ background: '#0091DD', opacity: isSendingEmail ? 0.7 : 1 }}
                      >
                        {isSendingEmail ? 'Sending…' : 'Forward to team'}
                      </button>
                      <button
                        onClick={() => setShowEmailPrompt(false)}
                        className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}

                {emailSent && (
                  <div className="text-center text-xs text-green-600 py-1">
                    ✅ Conversation forwarded to our team!
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Forward button (manual) */}
              {messages.length > 2 && !emailSent && !showEmailPrompt && (
                <div className="px-4 py-1.5 flex-shrink-0" style={{ background: '#f8fbff', borderTop: '1px solid #e8f4fd' }}>
                  <button
                    onClick={() => setShowEmailPrompt(true)}
                    className="text-xs text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    📧 Forward this conversation to our team →
                  </button>
                </div>
              )}

              {/* Input */}
              <form
                onSubmit={handleSend}
                className="flex items-center gap-2 px-3 py-3 flex-shrink-0"
                style={{ background: 'white', borderTop: '1px solid #e8f4fd' }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message…"
                  disabled={isLoading}
                  className="flex-1 px-3 py-2 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: '#f0f8ff',
                    border: '1.5px solid #cce5f5',
                    color: '#333',
                    fontSize: '14px',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#00A7FF')}
                  onBlur={(e) => (e.target.style.borderColor = '#cce5f5')}
                />
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className="flex items-center justify-center text-white rounded-xl transition-all active:scale-95"
                  style={{
                    width: '38px',
                    height: '38px',
                    background: isLoading || !input.trim() ? '#a0d4f5' : 'linear-gradient(135deg, #00A7FF, #0091DD)',
                    flexShrink: 0,
                  }}
                  aria-label="Send message"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
